import { Component, OnInit, ViewChild } from '@angular/core';
import { AlertController, IonModal, Platform } from '@ionic/angular';
import { App } from '@capacitor/app';
import { SharedService } from './shared/services/other/shared.service';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { AppConstants } from './shared/constants/constants';
import { BackButtonEvent, SESSION_STORAGE_KEYS } from './shared/types/types';
import { TranslatorService } from './shared/services/other/translator.service';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
})
export class AppComponent implements OnInit {
  allTranslateLanguages:any[] = []
  filteredLanguages:any[] = [];
  selectedLanguage = 'en'
  searchText: string = '';
  @ViewChild('modal') modal!: IonModal;
  showHeader: boolean = true;

  private readonly hiddenRoutes: string[] = AppConstants.HIDE_HEADER_ROUTES;
  showOpenAppButton: boolean = false;

  constructor(
    private platform: Platform,
    private sharedService: SharedService,
    private router: Router,
    private alertCtrl: AlertController,
    private translatorService : TranslatorService
  ) {
    this.platform.ready().then(() => {
      this.logDetectedPlatforms();
      this.registerBackButtonEvent();
      this.setupDeepLinkListener();
      // this.executePlatformSpecificCode();
    });
  }

  /**
   * Sets up a listener for incoming deep links (skillmantra://auth?token=xxx)
   * When the app is opened via deep link, extract the token and navigate to auth
   */
  private setupDeepLinkListener() {
    App.addListener('appUrlOpen', (event) => {
      console.log('Deep link received:', event.url);
      
      // Parse the URL to extract token
      const url = new URL(event.url);
      
      // Check if this is an auth deep link (skillmantra://auth?token=xxx)
      if (url.host === 'auth' || url.pathname === '/auth' || url.pathname === 'auth') {
        const token = url.searchParams.get('token');
        const isNewUser = url.searchParams.get('is_new_user') === 'true';
        
        console.log('Auth deep link - Token:', token, 'Is new user:', isNewUser);
        
        if (token) {
          // Navigate to auth page with the token
          this.router.navigate(['auth', token]);
        }
      }
    });
  }

  private logDetectedPlatforms() {
    const platforms = this.platform.platforms();
    console.log('Detected platforms:', platforms);
  }

  private executePlatformSpecificCode() {
    if (window.location.href.startsWith('http') || this.platform.is('pwa') || this.platform.is('desktop')) {
      console.log('Web or PWA specific code');
    } else if (this.platform.is('android')) {
      console.log('Android specific code');
    } else if (this.platform.is('ios')) {
      console.log('iOS specific code');
    } else if (this.platform.is('hybrid')) {
      console.log('Hybrid (native app) specific code');
    }
  }

  private registerBackButtonEvent() {
    document.addEventListener('ionBackButton', (ev: Event) => this.handleBackButtonEvent(ev));
  }

  private async handleBackButtonEvent(ev: Event) {
    const backButtonEvent = ev as CustomEvent<BackButtonEvent>;
    backButtonEvent.detail.register(-1, async () => {
      await this.showExitAppAlertIfNeeded();
    });
  }

  private async showExitAppAlertIfNeeded() {
    const currentRoute = this.router.url;
    if (this.isExitRoute(currentRoute)) {
      App.exitApp();
      return;
    }
    if (currentRoute === '/borrowers') {
      await this.presentExitAppAlert();
    }
  }

  private isExitRoute(route: string): boolean {
    return route === '/login' || route === '/signup';
  }

  private async presentExitAppAlert() {
    const alert = await this.alertCtrl.create({
      header: 'Exit App',
      message: 'Do you really want to close the app?',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
          handler: () => {
            this.router.navigateByUrl('/borrowers');
          },
        },
        {
          text: 'Close',
          handler: () => {
            App.exitApp();
          },
        },
      ],
    });
    await alert.present();
  }

  ngOnInit() {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => this.updateHeaderVisibility());
      this.getTheTranslateLanguage()
      this.selectedLanguage = this.sharedService.getDataFromSessionStorage(SESSION_STORAGE_KEYS.TARGET_LANGUAGE)
  }

  private updateHeaderVisibility() {
    const currentRoute = this.router.url;
    this.showHeader = !this.hiddenRoutes.some(route => currentRoute.includes(route));
  }
  openInApp() {
    const sessionToken = this.sharedService.getDataFromSessionStorage(SESSION_STORAGE_KEYS.SESSION_TOKEN)
    const appUrlScheme = `https://auth/${sessionToken.access}?refresh=${sessionToken.refresh}`;
    window.location.href = appUrlScheme;
  }

  getTheTranslateLanguage(){
    this.translatorService.getAllTranslateSupportedLanguages().subscribe((res:any)=>{
      for (const key in res.translation) {
        const element = res.translation[key];
        element['lan_key'] = key
        this.allTranslateLanguages.push(element)
        this.filteredLanguages.push(element)
      }
      console.log(this.allTranslateLanguages)
    })
  }

  filterLanguages() {
    const search = this.searchText.toLowerCase();
    this.filteredLanguages = this.allTranslateLanguages.filter(
      lan =>
        lan.nativeName.toLowerCase().includes(search) ||
        lan.lan_key.toLowerCase().includes(search) || 
        lan.name.toLowerCase().includes(search)
    );
  }

  applyLanguage() {
    if(this.selectedLanguage === 'en'){
      this.sharedService.removeDataToSessionStorage(SESSION_STORAGE_KEYS.TARGET_LANGUAGE)
      this.sharedService.removeDataToSessionStorage(SESSION_STORAGE_KEYS.CURRENT_LANGUAGE)
      this.translatorService.lanUpdates.next(this.selectedLanguage)
      this.modal.dismiss();
      return 
    }
    if (this.selectedLanguage) {
      this.sharedService.setDataToSessionStorage({key : SESSION_STORAGE_KEYS.CURRENT_LANGUAGE,data : this.sharedService.getDataFromSessionStorage(SESSION_STORAGE_KEYS.TARGET_LANGUAGE) || 'en'})
      this.sharedService.setDataToSessionStorage({key : SESSION_STORAGE_KEYS.TARGET_LANGUAGE,data : this.selectedLanguage})
      console.log('Selected Language:', this.selectedLanguage);
      this.translatorService.lanUpdates.next(this.selectedLanguage)
      this.modal.dismiss();
    }
  }
  resetLanguage(){
    this.selectedLanguage = 'en'
    this.sharedService.removeDataToSessionStorage(SESSION_STORAGE_KEYS.TARGET_LANGUAGE)
    this.sharedService.removeDataToSessionStorage(SESSION_STORAGE_KEYS.CURRENT_LANGUAGE)
    this.translatorService.lanUpdates.next(this.selectedLanguage)
    this.searchText = ''
    this.filteredLanguages = JSON.parse(JSON.stringify(this.allTranslateLanguages))
    this.modal.dismiss();
  }
}
